import { useCallback } from 'react'
import { useEnrollmentStore } from '@/stores'
import type { NavigateFn } from '../types'
import { SELF_ENROLLMENT_TRACKED_TOTAL_STEPS } from '../utils/steps'
import { useDocusignRuntimeState } from './runtimeState/useDocusignRuntimeState'
import { useFlowRuntimeState } from './runtimeState/useFlowRuntimeState'
import { useSessionStepSyncRuntime } from './runtimeState/useSessionStepSyncRuntime'

type UseSelfEnrollmentRuntimeStateArgs = {
  navigate: NavigateFn
  initialStep: number | null
  limitToStep: number | null
}

export function useSelfEnrollmentRuntimeState({
  navigate,
  initialStep,
  limitToStep
}: UseSelfEnrollmentRuntimeStateArgs) {
  const storedSessionToken = useEnrollmentStore(state => state.sessionToken)
  const setSessionToken = useEnrollmentStore(state => state.setSessionToken)
  const setEnrollmentId = useEnrollmentStore(state => state.setEnrollmentId)
  const setSignatureCompleted = useEnrollmentStore(state => state.setSignatureCompleted)
  const setEnrollmentCompleted = useEnrollmentStore(state => state.setEnrollmentCompleted)
  const updateEnrollmentStoreFormData = useEnrollmentStore(state => state.updateFormData)
  const updateUploadedDocumentFiles = useEnrollmentStore(state => state.updateUploadedDocumentFiles)
  const storedFormLicenseState = useEnrollmentStore(state => state.formData.licenseState)
  const storedFormLocationState = useEnrollmentStore(state => state.formData.locationState)
  const persistedCurrentStep = useEnrollmentStore(state => state.currentStep)
  const setPersistedCurrentStep = useEnrollmentStore(state => state.setCurrentStep)

  const enrollmentSessionToken = storedSessionToken?.trim() ?? ''
  const flowRuntime = useFlowRuntimeState({
    initialStep,
    navigate,
    persistedCurrentStep,
    setPersistedCurrentStep
  })
  const docusignRuntime = useDocusignRuntimeState()
  const sessionStepSyncRuntime = useSessionStepSyncRuntime()

  const resetAnonymousEnrollmentAndReturnToOptions = useCallback(() => {
    flowRuntime.resetAnonymousEnrollmentAndReturnToOptions()
    docusignRuntime.resetDocusignRuntime()
  }, [docusignRuntime, flowRuntime])

  const navigation = {
    navigate,
    goToStep: flowRuntime.goToStep
  }
  const formState = {
    fieldEditability: flowRuntime.fieldEditability,
    formData: flowRuntime.formData,
    formDataRef: flowRuntime.formDataRef,
    setFieldEditability: flowRuntime.setFieldEditability,
    setFormData: flowRuntime.setFormData
  }
  const sessionState = {
    enrollmentSessionToken,
    setSessionToken,
    setEnrollmentId,
    setPersistedCurrentStep,
    setSignatureCompleted,
    setEnrollmentCompleted
  }
  const storeBindings = {
    updateEnrollmentStoreFormData,
    updateUploadedDocumentFiles
  }
  const stepState = {
    currentStep: flowRuntime.currentStep,
    totalSteps: SELF_ENROLLMENT_TRACKED_TOTAL_STEPS,
    limitToStep
  }
  const stepNavigation = {
    ...navigation,
    scrollToTop: flowRuntime.scrollToTop,
    resetAnonymousEnrollmentAndReturnToOptions,
    suppressNextSessionStepSync: sessionStepSyncRuntime.suppressNextSessionStepSync,
    setSessionStepSyncLock: sessionStepSyncRuntime.setSessionStepSyncLock,
    setUploadDocumentsSkipped: sessionStepSyncRuntime.setUploadDocumentsSkipped,
    consumeUploadDocumentsSkipped: sessionStepSyncRuntime.consumeUploadDocumentsSkipped
  }
  const stepActionSetters = {
    setStepSubmissionError: flowRuntime.setStepSubmissionError,
    setStepValidationError: flowRuntime.setStepValidationError,
    setIsCheckingStepAccess: flowRuntime.setIsCheckingStepAccess
  }
  const docusignState = {
    isDocusignModalOpen: docusignRuntime.isDocusignModalOpen,
    isFinalizingDocusign: docusignRuntime.isFinalizingDocusign,
    isSignatureSigned: docusignRuntime.isSignatureSigned,
    docusignUrl: docusignRuntime.docusignUrl
  }
  const docusignSetters = {
    setStepSubmissionError: flowRuntime.setStepSubmissionError,
    setDocusignUrl: docusignRuntime.setDocusignUrl,
    setDocusignFrameKey: docusignRuntime.setDocusignFrameKey,
    setIsDocusignIframeLoading: docusignRuntime.setIsDocusignIframeLoading,
    setIsFinalizingDocusign: docusignRuntime.setIsFinalizingDocusign,
    setIsDocusignModalOpen: docusignRuntime.setIsDocusignModalOpen,
    setSignatureStatus: docusignRuntime.setSignatureStatus,
    setIsSignatureSigned: docusignRuntime.setIsSignatureSigned,
    setIsPreparingDocusign: docusignRuntime.setIsPreparingDocusign,
    setIsCheckingSignature: docusignRuntime.setIsCheckingSignature
  }
  const docusignRefs = {
    hasLoadedDocusignIframeRef: docusignRuntime.hasLoadedDocusignIframeRef,
    hasBlurredDuringDocusignRef: docusignRuntime.hasBlurredDuringDocusignRef,
    isRestartingDocusignRef: docusignRuntime.isRestartingDocusignRef,
    isPreparingDocusignRef: docusignRuntime.isPreparingDocusignRef
  }

  return {
    currentStep: flowRuntime.currentStep,
    fieldEditability: flowRuntime.fieldEditability,
    setCurrentStep: flowRuntime.setCurrentStep,
    persistedCurrentStep,
    formData: flowRuntime.formData,
    updateFormData: flowRuntime.updateFormData,
    stepSubmissionError: flowRuntime.stepSubmissionError,
    setStepSubmissionError: flowRuntime.setStepSubmissionError,
    stepValidationError: flowRuntime.stepValidationError,
    setStepValidationError: flowRuntime.setStepValidationError,
    docusignUrl: docusignRuntime.docusignUrl,
    docusignFrameKey: docusignRuntime.docusignFrameKey,
    isDocusignModalOpen: docusignRuntime.isDocusignModalOpen,
    signatureStatus: docusignRuntime.signatureStatus,
    setSignatureStatus: docusignRuntime.setSignatureStatus,
    isSignatureSigned: docusignRuntime.isSignatureSigned,
    setIsSignatureSigned: docusignRuntime.setIsSignatureSigned,
    isPreparingDocusign: docusignRuntime.isPreparingDocusign,
    isDocusignIframeLoading: docusignRuntime.isDocusignIframeLoading,
    isFinalizingDocusign: docusignRuntime.isFinalizingDocusign,
    isCheckingSignature: docusignRuntime.isCheckingSignature,
    isCheckingStepAccess: flowRuntime.isCheckingStepAccess,
    stepContentRef: flowRuntime.stepContentRef,
    checkingStepRef: flowRuntime.checkingStepRef,
    validationMessageRef: flowRuntime.validationMessageRef,
    suppressNextSessionStepSyncRef: sessionStepSyncRuntime.suppressNextSessionStepSyncRef,
    sessionStepSyncLockRef: sessionStepSyncRuntime.sessionStepSyncLockRef,
    storedFormLicenseState,
    storedFormLocationState,
    enrollmentSessionToken,
    setSignatureCompleted,
    resetAnonymousEnrollmentAndReturnToOptions,
    scrollToTop: flowRuntime.scrollToTop,
    goToStep: flowRuntime.goToStep,
    formState,
    sessionState,
    storeBindings,
    stepState,
    navigation,
    stepNavigation,
    stepActionSetters,
    docusignState,
    docusignSetters,
    docusignRefs
  }
}
